import { useRef, useState } from 'react';
import { useGT } from 'gt-react';
import { RecipientChip } from '@/components/common/email-composer/RecipientChip';
import {
  EMAIL_REGEX,
  makeRecipientId,
} from '@/components/common/email-composer/types';
import { cn } from '@/lib/utils';
import type { ComposeContact } from './composeReplyData';

interface ComposeRecipientFieldProps {
  /** Short label shown to the left of the field (To / Cc / Bcc). */
  label: string;
  /** Contacts offered in the typeahead dropdown. */
  contacts: ComposeContact[];
  /** Currently selected recipients. */
  recipients: ComposeContact[];
  onChange: (next: ComposeContact[]) => void;
  /**
   * Allow committing a free-typed email that isn't in `contacts`. Defaults to
   * true (compose To/Cc/Bcc). Set false to restrict to dropdown picks only.
   */
  allowCustom?: boolean;
}

/**
 * A single editable recipient row. Type to filter a dropdown of contacts;
 * clicking a suggestion (or pressing Enter on a valid typed email) adds a
 * removable pill. Mirrors the keyboard/dropdown UX of the email-composer's
 * RecipientRow and reuses its RecipientChip for the pills.
 */
export function ComposeRecipientField({
  label,
  contacts,
  recipients,
  onChange,
  allowCustom = true,
}: ComposeRecipientFieldProps) {
  const gt = useGT();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [hasError, setHasError] = useState(false);
  // A mousedown inside the suggestion list pre-empts the input's blur so the
  // click reliably runs before the dropdown tears down.
  const suppressBlurRef = useRef(false);

  const selectedEmails = new Set(recipients.map((r) => r.email.toLowerCase()));
  // Addresses known to the org (members + address book) — anything else is a
  // free-typed external recipient and gets flagged on its pill.
  const contactEmails = new Set(contacts.map((c) => c.email.toLowerCase()));
  const trimmedQuery = query.trim().toLowerCase();
  const suggestions = contacts.filter(
    (c) =>
      !selectedEmails.has(c.email.toLowerCase()) &&
      (trimmedQuery === '' ||
        c.name.toLowerCase().includes(trimmedQuery) ||
        c.email.toLowerCase().includes(trimmedQuery)),
  );
  const showSuggestions = isFocused && suggestions.length > 0;
  const safeHighlight = Math.min(
    highlightedIndex,
    Math.max(0, suggestions.length - 1),
  );

  function addRecipient(contact: ComposeContact) {
    if (selectedEmails.has(contact.email.toLowerCase())) return;
    onChange([...recipients, contact]);
    setQuery('');
    setHasError(false);
    setHighlightedIndex(0);
    // Collapse the dropdown after a pick; typing in the field reopens it.
    setIsFocused(false);
    suppressBlurRef.current = false;
  }

  function commitTypedEmail() {
    if (!allowCustom) return;
    const value = query.trim();
    if (value === '') return;
    if (!EMAIL_REGEX.test(value) || selectedEmails.has(value.toLowerCase())) {
      setHasError(true);
      return;
    }
    onChange([
      ...recipients,
      { id: makeRecipientId(), name: value, email: value },
    ]);
    setQuery('');
    setHasError(false);
    setHighlightedIndex(0);
  }

  function removeRecipient(id: string) {
    onChange(recipients.filter((r) => r.id !== id));
  }

  return (
    <div className="flex items-start gap-2 px-3 py-1.5">
      <span className="w-9 shrink-0 pt-1.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
      <div className="relative flex-1">
        <div className="flex min-h-8 flex-wrap items-center gap-1.5 rounded-md px-1 py-1 focus-within:bg-muted/40">
          {recipients.map((r) => (
            <RecipientChip
              key={r.id}
              email={r.name}
              external={!contactEmails.has(r.email.toLowerCase())}
              onRemove={() => removeRecipient(r.id)}
            />
          ))}
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHasError(false);
              setHighlightedIndex(0);
              setIsFocused(true);
            }}
            onFocus={() => {
              setIsFocused(true);
              setHighlightedIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && showSuggestions) {
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % suggestions.length);
              } else if (e.key === 'ArrowUp' && showSuggestions) {
                e.preventDefault();
                setHighlightedIndex(
                  (i) => (i - 1 + suggestions.length) % suggestions.length,
                );
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (showSuggestions && suggestions[safeHighlight]) {
                  addRecipient(suggestions[safeHighlight]);
                } else {
                  commitTypedEmail();
                }
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setQuery('');
                setIsFocused(false);
              } else if (
                e.key === 'Backspace' &&
                query === '' &&
                recipients.length > 0
              ) {
                removeRecipient(recipients[recipients.length - 1].id);
              }
            }}
            onBlur={() => {
              if (suppressBlurRef.current) {
                suppressBlurRef.current = false;
                return;
              }
              // Commit a valid typed email on blur; otherwise just close.
              if (query.trim() !== '' && EMAIL_REGEX.test(query.trim())) {
                commitTypedEmail();
              }
              setIsFocused(false);
            }}
            placeholder={
              recipients.length === 0
                ? gt('Add recipients...', {
                    context: 'compose reply recipient field placeholder',
                  })
                : ''
            }
            className={cn(
              'h-6 min-w-[160px] flex-1 border-0 bg-transparent text-sm outline-hidden',
              hasError && 'text-red-600',
            )}
          />
        </div>
        {showSuggestions && (
          <ul
            role="listbox"
            className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover shadow-md"
          >
            {suggestions.map((s, i) => (
              <li
                key={s.id}
                role="option"
                aria-selected={i === safeHighlight}
                onMouseDown={() => {
                  suppressBlurRef.current = true;
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
                onClick={() => addRecipient(s)}
                className={cn(
                  'flex cursor-pointer flex-col gap-0.5 px-3 py-1.5',
                  i === safeHighlight && 'bg-muted',
                )}
              >
                <span className="truncate text-sm font-medium">{s.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {s.email}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
